import { useSearchParams } from "react-router-dom";
import { usePaginate } from "~/components/pagination/usePaginate";
import {
  AggregateScoreOrder,
  AggregateScoreOrderField,
  AggregateScoreType,
  OrderDirection,
  ScoreRating,
  useGetAggregateScoresQuery,
} from "~/operations";
import { useSort } from "~/pages/inventory/hooks/useSort";
import { MetricRatings } from "~/lib/arrow";

export interface UseSlasDashboardProps {
  scopeMrn?: string;
}

export function useSlasDashboard({ scopeMrn }: UseSlasDashboardProps) {
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);

  const sort = useSort<AggregateScoreOrder>({
    baseUrl,
    defaultSort: {
      field: AggregateScoreOrderField.SlaOverdueCount,
      direction: OrderDirection.Desc,
    },
    validFields: [
      AggregateScoreOrderField.SlaOverdueCount,
      AggregateScoreOrderField.SlaAtRiskCount,
      AggregateScoreOrderField.RiskValue,
      AggregateScoreOrderField.Title,
    ],
  });

  const [searchParams] = useSearchParams();

  const riskRatingParams = searchParams.getAll("riskRating");
  const activeRatings = riskRatingParams.flatMap((value) =>
    isRiskRating(value) ? value : [],
  );

  const { data, loading, error } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: scopeMrn ?? "",
      filter: {
        scoreTypes: [
          AggregateScoreType.Advisory,
          AggregateScoreType.Check,
          AggregateScoreType.Vulnerability,
        ],
        hasAnySla: true,
        rating: activeRatings,
      },
      orderBy: sort.orderBy,
      ...paginate.pageVars,
    },
    skip: !scopeMrn,
  });

  const aggScoresUnion = data?.aggregateScores;
  const aggScoresConnection =
    aggScoresUnion?.__typename === "AggregateScoresConnection"
      ? aggScoresUnion
      : undefined;
  const edges =
    aggScoresConnection?.edges?.flatMap((e) => (e.node ? e : [])) ?? [];
  const totalCount = aggScoresConnection?.totalCount ?? 0;
  const empty = !loading && totalCount === 0;
  const paginateProps = paginate.pageProps(aggScoresConnection?.pageInfo);

  return {
    loading,
    error,
    empty,
    activeRatings,
    edges,
    totalCount,
    sort,
    paginateProps,
    baseUrl,
  };
}

export function isRiskRating(value: any): value is ScoreRating {
  return Object.values(ScoreRating).includes(value);
}

export function metricRatingToRiskRating(value: MetricRatings): ScoreRating {
  const rating = value.toUpperCase();
  return isRiskRating(rating) ? rating : ScoreRating.Unscored;
}
